import React, { useContext, useEffect, useRef, useState } from "react";
import { GoogleGenAI, Type } from "@google/genai";
import {
    get_current_time,
    get_diet_details,
    get_workout_details,
} from "./tools";
import { DataContext } from "../DataContext";
import DietData from "../data/DietData";
import WorkoutData from "../data/WorkoutData";

const BotWidget = () => {
    const last = useRef(null);
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("Hi");
    const [messages, setMessages] = useState([]);
    const [streamingMessage, setStreamingMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const { data } = useContext(DataContext);

    const get_user_details = () => data;

    const get_diet_by_time = ({ time }) => {
        const tmp = JSON.parse(data.preferedDiet);
        return DietData.filter(
            (item) => item.time === time && tmp.includes(item.name)
        );
    };
    const get_todays_workout = () => {
        const date = new Date().getDay();
        const day = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ];
        const dayName = day[date];

        const goal = data.goal;
        const tmp = JSON.parse(data.preferedWorkOut);
        const today = WorkoutData.filter((item) => tmp.includes(item.name));

        let todaySchedule = [];

        if (dayName === "Monday") {
            todaySchedule = today.filter(
                (exercise) =>
                    exercise.bodyPart === "back" ||
                    (exercise.bodyPart === "cardio" && goal === "Loss weight")
            );
        } else if (dayName === "Tuesday") {
            todaySchedule = today.filter(
                (exercise) =>
                    exercise.bodyPart === "lower arms" ||
                    exercise.bodyPart === "upper arms" ||
                    (exercise.bodyPart === "cardio" && goal === "Loss weight")
            );
        } else if (dayName === "Wednesday") {
            todaySchedule = today.filter(
                (exercise) =>
                    exercise.bodyPart === "chest" ||
                    (exercise.bodyPart === "cardio" && goal === "Loss weight")
            );
        } else if (dayName === "Thursday") {
            todaySchedule = today.filter(
                (exercise) =>
                    exercise.bodyPart === "lower arms" ||
                    exercise.bodyPart === "upper arms" ||
                    (exercise.bodyPart === "cardio" && goal === "Loss weight")
            );
        } else if (dayName === "Friday") {
            todaySchedule = today.filter(
                (exercise) =>
                    exercise.bodyPart === "shoulders" ||
                    (exercise.bodyPart === "cardio" && goal === "Loss weight")
            );
        } else if (dayName === "Saturday") {
            todaySchedule = today.filter(
                (exercise) =>
                    exercise.bodyPart === "lower legs" ||
                    exercise.bodyPart === "neck" ||
                    exercise.bodyPart === "waist" ||
                    exercise.bodyPart === "upper legs" ||
                    (exercise.bodyPart === "cardio" && goal === "Loss weight")
            );
        } else if (dayName === "Sunday") {
            todaySchedule = [];
        }

        return todaySchedule;
    };

    const toolFunctions = {
        get_user_details,
        get_workout_details,
        get_diet_details,
        get_current_time,
        get_diet_by_time,
        get_todays_workout,
    };

    const ai = new GoogleGenAI({
        apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    });

    const tools = [
        {
            functionDeclarations: [
                {
                    name: "get_user_details",
                    description:
                        "Function returns the information about current user.like age diet weight height prefered workout.",
                    parameters: {},
                },
                { name: "get_current_time", parameters: {} },
                { name: "get_todays_workout", parameters: {} },
                {
                    name: "get_workout_details",
                    parameters: {
                        type: Type.OBJECT,
                        properties: { workout: { type: Type.STRING } },
                        required: ["workout"],
                    },
                },
                {
                    name: "get_diet_by_time",
                    parameters: {
                        type: Type.OBJECT,
                        properties: { time: { type: Type.STRING } },
                        required: ["time"],
                    },
                },
                {
                    name: "get_diet_details",
                    parameters: {
                        type: Type.OBJECT,
                        properties: { diet: { type: Type.STRING } },
                        required: ["diet"],
                    },
                },
            ],
        },
    ];

    const systemInstruction = `You are FitBuddy an AI assistant designed to help users with diet and workout-related queries  
You can call functions to retrieve specific details and may use the output of one function as input for another to ensure accurate responses  
Always validate your replies by executing the necessary functions until the desired result is achieved  
Respond in a friendly conversational tone  
Ensure your answers are concise relevant and directly address the user's query  
If the query is unclear or unsupported politely ask for clarification or suggest related topics you can help with  
Double check your responses before replying to the user  
If you are uncertain about something request more information or suggest helpful alternatives  
You may also use the available tools to gather more information about the user's request  
If a valid answer cannot be generated retry using a different approach or ask the user for additional details  
Always respond exactly to the user's question do not include extra or unrelated information  

The get_user_details function returns current user information including age diet weight height and preferred workouts  
The get_diet_details function supports only the following diets ${data?.preferedDiet}  
When calling this function ensure the diet name matches one from the above list exactly  

If the user asks for a diet suggestion use the get_diet_by_time function  
This function only accepts one of the following time values  
['early morning' 'break fast' 'lunch' 'snaks' 'dinner' 'bed time']  

Use the get_current_time function to determine the time in HH MM format and select the appropriate time category from the list above  

The get_workout_details function supports only the following workouts ${data?.preferedWorkOut}  
Make sure the workout name passed is one from this list  

If the user asks can you suggest me a diet respond using the result from the get_diet_by_time function based on the current time  

If the user asks who created you or who is your creator respond with  
I was created by the FitBuddy Team
and I am here to assist you with your diet and workout queries.

get_todays_workout function returns the workout schedule for today based on the user's preferred workouts and fitness goal
If the user asks what is my workout for today respond with the result from the get_todays_workout function based on the current day and user's preferred workouts and fitness goal`;

    const handleSendMessage = async () => {
        if (!input.trim()) return;

        const newMessage = { role: "user", parts: [{ text: input }] };
        setMessages((prev) => [...prev, newMessage]);
        setInput("");
        setStreamingMessage("");
        setLoading(true);
        await processAIResponse([...messages, newMessage]);
    };

    const processAIResponse = async (currentContents, retries = 0) => {
        const stream = await ai.models.generateContentStream({
            model: "gemini-2.5-flash",
            contents: currentContents,
            config: { tools, systemInstruction },
        });

        let fullMessage = "";
        for await (const chunk of stream) {
            const text = chunk?.candidates?.[0]?.content?.parts?.[0]?.text;
            if (text) {
                const words = text.split(" ");
                if (
                    messages.length > 0 &&
                    messages[messages.length - 1].role === "model"
                ) {
                    const lastMessage =
                        messages[messages.length - 1].parts[0].text;
                    speak(lastMessage);
                }
                for (const word of words) {
                    fullMessage += word + " ";
                    setStreamingMessage(fullMessage.trim());
                    await new Promise((res) => setTimeout(res, 40));
                }
            }

            if (chunk.functionCalls?.length > 0) {
                const { name, args } = chunk.functionCalls[0];
                const toolResponse = toolFunctions[name](args);
                const toolPart = {
                    role: "user",
                    parts: [
                        {
                            functionResponse: {
                                name,
                                response: { result: toolResponse },
                            },
                        },
                    ],
                };
                setMessages((prev) => [...prev, toolPart]);
                await processAIResponse([...currentContents, toolPart]);
                return;
            }
        }

        setMessages((prev) => [
            ...prev,
            { role: "model", parts: [{ text: fullMessage.trim() }] },
        ]);
        setStreamingMessage("");
        setLoading(false);
    };
    useEffect(() => {
        if (last.current) {
            last.current.scrollIntoView({ behavior: "smooth" }); //move to bottom of msglist on update mmsglist
        }
    }, [messages, streamingMessage]);
    const speak = (text) => {
        const utterance = new SpeechSynthesisUtterance(text);
        speechSynthesis.speak(utterance);
    };

    return (
        <div className="bot-widget-container">
            <button className="bot-button" onClick={() => setOpen(!open)}>
                <img src="../images/intro.png" alt="Bot" />
            </button>
            {open && (
                <div className="chat-window">
                    <div className="chat-header">FitBuddy AI</div>
                    <div className="chat-messages">
                        {messages.map((msg, i) =>
                            msg.parts?.[0]?.text ? (
                                <div
                                    key={i}
                                    className={`message ${
                                        msg.role === "user" ? "user" : "bot"
                                    }`}>
                                    {msg.parts[0].text}
                                </div>
                            ) : null
                        )}

                        {streamingMessage && (
                            <div className="message bot thinking">
                                {streamingMessage}
                            </div>
                        )}
                        {loading && !streamingMessage && (
                            <div className="thinking-dots">
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </div>
                        )}
                        <div ref={last}></div>
                    </div>
                    <div className="chat-input">
                        <input
                            type="text"
                            placeholder="Ask something..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleSendMessage()
                            }
                        />
                        <button onClick={handleSendMessage}>Send</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BotWidget;
